import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import { supabase } from '../../lib/supabase';

const COMPANIES = [
  { value: 'sugarhathi', label: 'SugarHathi (Villgrains Farmtech)' },
  { value: 'krishilinks', label: 'KrishiLinks' },
];

const DOC_TYPES = [
  { value: 'rate_request_letter', label: 'Rate Request Letter' },
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'proforma_invoice', label: 'Proforma Invoice' },
];

const emptyItem = () => ({ id: Date.now() + Math.random(), description: '', hsn_code: '', qty: '', unit: 'MT', rate: '' });

const emptyForm = {
  document_type: 'rate_request_letter',
  sending_company: '',
  // Rate Request Letter fields
  recipient_email: '',
  recipient_name: '',
  recipient_address: '',
  commodity_name: '',
  quantity_mt: '',
  grade: '',
  // Purchase Order fields
  supplier_email: '',
  supplier_name: '',
  supplier_address: '',
  supplier_gstin: '',
  gst_rate_percent: '5',
  delivery_terms: '',
  payment_terms: '',
  items: [emptyItem()],
  // Proforma Invoice fields
  buyer_email: '',
  buyer_name: '',
  buyer_address: '',
  buyer_gstin: '',
  valid_till: '',
  // Shared
  share_contact_number: 'no',
  contact_number: '',
};

const inputStyle = { borderColor: '#e5e7eb' };
const Label = ({ children, required }) => (
  <label className="block text-xs font-bold mb-1.5" style={{ color: '#16354f' }}>
    {children} {required && <span style={{ color: '#DC143C' }}>*</span>}
  </label>
);

const FroEmailCenter = () => {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateItem = (id, field, value) => setForm(prev => ({ ...prev, items: prev.items.map(it => it.id === id ? { ...it, [field]: value } : it) }));
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeItem = (id) => setForm(prev => prev.items.length === 1 ? prev : { ...prev, items: prev.items.filter(it => it.id !== id) });

  const isPO = form?.document_type === 'purchase_order';
  const isPI = form?.document_type === 'proforma_invoice';

  const itemsValid = (isPO || isPI) && form?.items?.every(it => it.description?.trim() && Number(it.qty) > 0 && Number(it.rate) > 0);

  const isValid = form?.document_type && form?.sending_company &&
    !(isPI && form?.sending_company === 'krishilinks') &&
    (form?.share_contact_number === 'no' || form?.contact_number?.trim()) &&
    (isPO
      ? form?.supplier_email?.trim() && form?.supplier_name?.trim() && itemsValid
      : isPI
      ? form?.buyer_email?.trim() && form?.buyer_name?.trim() && itemsValid
      : form?.recipient_email?.trim() && form?.recipient_name?.trim() && form?.commodity_name?.trim() && Number(form?.quantity_mt) > 0);

  const subtotal = (isPO || isPI) ? form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0) : 0;
  const taxAmount = (isPO || isPI) ? subtotal * (Number(form.gst_rate_percent) || 0) / 100 : 0;

  const handleSend = async () => {
    if (!isValid || sending) return;
    setSending(true);
    setResult(null);
    try {
      const payload = isPO ? {
        action: 'send',
        email_type: 'purchase_order',
        sending_company: form?.sending_company,
        supplier_email: form?.supplier_email?.trim(),
        supplier_name: form?.supplier_name?.trim(),
        supplier_address: form?.supplier_address?.trim(),
        supplier_gstin: form?.supplier_gstin?.trim() || null,
        items: form?.items?.map(it => ({ description: it.description?.trim(), hsn_code: it.hsn_code?.trim(), qty: Number(it.qty), unit: it.unit || 'MT', rate: Number(it.rate) })),
        gst_rate_percent: Number(form?.gst_rate_percent) || 0,
        delivery_terms: form?.delivery_terms?.trim() || null,
        payment_terms: form?.payment_terms?.trim() || null,
        share_contact_number: form?.share_contact_number === 'yes',
        contact_number: form?.share_contact_number === 'yes' ? form?.contact_number?.trim() : null,
      } : isPI ? {
        action: 'send',
        email_type: 'proforma_invoice',
        sending_company: form?.sending_company,
        buyer_email: form?.buyer_email?.trim(),
        buyer_name: form?.buyer_name?.trim(),
        buyer_address: form?.buyer_address?.trim(),
        buyer_gstin: form?.buyer_gstin?.trim() || null,
        items: form?.items?.map(it => ({ description: it.description?.trim(), hsn_code: it.hsn_code?.trim(), qty: Number(it.qty), unit: it.unit || 'MT', rate: Number(it.rate) })),
        gst_rate_percent: Number(form?.gst_rate_percent) || 0,
        valid_till: form?.valid_till || null,
        share_contact_number: form?.share_contact_number === 'yes',
        contact_number: form?.share_contact_number === 'yes' ? form?.contact_number?.trim() : null,
      } : {
        action: 'send',
        email_type: 'rate_request_letter',
        sending_company: form?.sending_company,
        recipient_email: form?.recipient_email?.trim(),
        recipient_name: form?.recipient_name?.trim(),
        recipient_address: form?.recipient_address?.trim(),
        commodity_name: form?.commodity_name?.trim(),
        quantity_mt: Number(form?.quantity_mt),
        grade: form?.grade?.trim() || null,
        share_contact_number: form?.share_contact_number === 'yes',
        contact_number: form?.share_contact_number === 'yes' ? form?.contact_number?.trim() : null,
      };

      const { data, error } = await supabase.functions.invoke('send-fro-email', { body: payload });
      if (error || data?.error) {
        setResult({ success: false, message: data?.error || error?.message || 'Failed to send' });
      } else {
        setResult({ success: true, pdf_url: data?.pdf_url, po_number: data?.po_number, invoice_number: data?.invoice_number });
        setForm(emptyForm);
      }
    } catch (e) {
      setResult({ success: false, message: e?.message || 'Failed to send' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2', fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet><title>Send Email – SugarHathi</title></Helmet>
      <RoleBasedHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5">
          <h1 className="text-2xl sm:text-3xl font-extrabold mb-1" style={{ color: '#16354f' }}>Send Email</h1>
          <p className="text-sm" style={{ color: '#52606d' }}>Compose and send official correspondence directly from SugarHathi.</p>
        </div>

        <div className="bg-white rounded-xl p-5 border" style={{ borderColor: '#f3e8d0' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label required>Document Type</Label>
              <select value={form?.document_type} onChange={(e) => update('document_type', e.target.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle}>
                {DOC_TYPES?.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
              </select>
            </div>
            <div>
              <Label required>Sending Company</Label>
              <select value={form?.sending_company} onChange={(e) => update('sending_company', e.target.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle}>
                <option value="">Select company...</option>
                {COMPANIES?.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <hr className="mb-4" style={{ borderColor: '#f3e8d0' }} />

          {isPI && form?.sending_company === 'krishilinks' && (
            <div className="mb-4 text-xs rounded-lg p-3" style={{ background: '#fef3c7', color: '#92400e' }}>
              Proforma Invoice isn't available for KrishiLinks yet — GSTIN and bank details haven't been added. Choose SugarHathi, or a different document type.
            </div>
          )}

          {!isPO && !isPI && (
            <>
              <div className="mb-4">
                <Label required>Recipient Email</Label>
                <input type="email" value={form?.recipient_email} onChange={(e) => update('recipient_email', e.target.value)} placeholder="factory@example.com" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label required>Recipient Company Name</Label>
                <input type="text" value={form?.recipient_name} onChange={(e) => update('recipient_name', e.target.value)} placeholder="Factory / vendor name" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Recipient Address <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                <textarea value={form?.recipient_address} onChange={(e) => update('recipient_address', e.target.value)} placeholder="Line 1&#10;Line 2&#10;City, State, PIN" rows={3} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <hr className="mb-4" style={{ borderColor: '#f3e8d0' }} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label required>Commodity Name</Label>
                  <input type="text" value={form?.commodity_name} onChange={(e) => update('commodity_name', e.target.value)} placeholder="e.g. Sugar S-30" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
                </div>
                <div>
                  <Label required>Quantity (MT)</Label>
                  <input type="number" value={form?.quantity_mt} onChange={(e) => update('quantity_mt', e.target.value)} placeholder="e.g. 100" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
                </div>
                <div>
                  <Label>Grade <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                  <input type="text" value={form?.grade} onChange={(e) => update('grade', e.target.value)} placeholder="e.g. S-30" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
                </div>
              </div>
            </>
          )}

          {isPO && (
            <>
              <div className="mb-4">
                <Label required>Supplier Email</Label>
                <input type="email" value={form?.supplier_email} onChange={(e) => update('supplier_email', e.target.value)} placeholder="supplier@example.com" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label required>Supplier Name</Label>
                <input type="text" value={form?.supplier_name} onChange={(e) => update('supplier_name', e.target.value)} placeholder="Supplier / vendor name" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Supplier Address <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                <textarea value={form?.supplier_address} onChange={(e) => update('supplier_address', e.target.value)} rows={2} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Supplier GSTIN <span className="font-normal" style={{ color: '#9ca3af' }}>(optional — determines CGST+SGST vs IGST)</span></Label>
                <input type="text" value={form?.supplier_gstin} onChange={(e) => update('supplier_gstin', e.target.value.toUpperCase())} placeholder="e.g. 29ABCDE1234F1Z5" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
            </>
          )}

          {isPI && (
            <>
              <div className="mb-4">
                <Label required>Buyer Email</Label>
                <input type="email" value={form?.buyer_email} onChange={(e) => update('buyer_email', e.target.value)} placeholder="buyer@example.com" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label required>Buyer Name</Label>
                <input type="text" value={form?.buyer_name} onChange={(e) => update('buyer_name', e.target.value)} placeholder="Buyer name" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Buyer Address <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                <textarea value={form?.buyer_address} onChange={(e) => update('buyer_address', e.target.value)} rows={2} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Buyer GSTIN <span className="font-normal" style={{ color: '#9ca3af' }}>(optional — determines CGST+SGST vs IGST)</span></Label>
                <input type="text" value={form?.buyer_gstin} onChange={(e) => update('buyer_gstin', e.target.value.toUpperCase())} placeholder="e.g. 29ABCDE1234F1Z5" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Valid Till <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                <input type="date" value={form?.valid_till} onChange={(e) => update('valid_till', e.target.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
            </>
          )}

          {(isPO || isPI) && (
            <>
              <hr className="mb-4" style={{ borderColor: '#f3e8d0' }} />
              <Label required>Items</Label>
              <div className="space-y-3 mb-3">
                {form?.items?.map((it, idx) => (
                  <div key={it.id} className="rounded-lg border p-3" style={inputStyle}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold" style={{ color: '#16354f' }}>Item {idx + 1}</span>
                      <button onClick={() => removeItem(it.id)} disabled={form.items.length === 1} className="text-xs font-bold" style={{ color: form.items.length === 1 ? '#d1d5db' : '#DC143C' }}>Remove</button>
                    </div>
                    <div className="mb-2">
                      <label className="block text-[10px] font-semibold mb-1" style={{ color: '#9ca3af' }}>Description</label>
                      <input value={it.description} onChange={(e) => updateItem(it.id, 'description', e.target.value)} placeholder="e.g. Sugar S-30" className="w-full rounded-lg border px-2.5 py-2 text-sm" style={inputStyle} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="block text-[10px] font-semibold mb-1" style={{ color: '#9ca3af' }}>HSN Code</label>
                        <input value={it.hsn_code} onChange={(e) => updateItem(it.id, 'hsn_code', e.target.value)} placeholder="e.g. 1701" className="w-full rounded-lg border px-2.5 py-2 text-sm" style={inputStyle} />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold mb-1" style={{ color: '#9ca3af' }}>Quantity</label>
                        <input type="number" value={it.qty} onChange={(e) => updateItem(it.id, 'qty', e.target.value)} placeholder="e.g. 10" className="w-full rounded-lg border px-2.5 py-2 text-sm" style={inputStyle} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold mb-1" style={{ color: '#9ca3af' }}>Unit</label>
                        <input value={it.unit} onChange={(e) => updateItem(it.id, 'unit', e.target.value)} placeholder="MT" className="w-full rounded-lg border px-2.5 py-2 text-sm" style={inputStyle} />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold mb-1" style={{ color: '#9ca3af' }}>Rate (₹ per unit)</label>
                        <input type="number" value={it.rate} onChange={(e) => updateItem(it.id, 'rate', e.target.value)} placeholder="e.g. 4500" className="w-full rounded-lg border px-2.5 py-2 text-sm" style={inputStyle} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={addItem} className="text-xs font-bold mb-4" style={{ color: '#1e4a72' }}>+ Add Item</button>

              <hr className="mb-4" style={{ borderColor: '#f3e8d0' }} />
              <div className="mb-4">
                <Label required>GST Rate (%)</Label>
                <input type="number" value={form?.gst_rate_percent} onChange={(e) => update('gst_rate_percent', e.target.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              {subtotal > 0 && (
                <div className="mb-4 text-xs rounded-lg p-3" style={{ background: '#f9fafb', color: '#52606d' }}>
                  Subtotal: ₹{subtotal.toLocaleString('en-IN')} &nbsp;+&nbsp; Tax ({form.gst_rate_percent}%): ₹{taxAmount.toLocaleString('en-IN')} &nbsp;=&nbsp;
                  <strong style={{ color: '#16354f' }}> Total: ₹{(subtotal + taxAmount).toLocaleString('en-IN')}</strong>
                </div>
              )}
            </>
          )}

          {isPO && (
            <>
              <div className="mb-4">
                <Label>Delivery Terms <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                <textarea value={form?.delivery_terms} onChange={(e) => update('delivery_terms', e.target.value)} rows={2} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
              <div className="mb-4">
                <Label>Payment Terms <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></Label>
                <textarea value={form?.payment_terms} onChange={(e) => update('payment_terms', e.target.value)} rows={2} className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
              </div>
            </>
          )}

          {isPI && (
            <div className="mb-4 text-xs rounded-lg p-3" style={{ background: '#f0fdf4', color: '#166534' }}>
              Bank details for payment are added automatically on the invoice — no need to enter them here.
            </div>
          )}

          <hr className="mb-4" style={{ borderColor: '#f3e8d0' }} />

          <div className="mb-2">
            <Label>Share your contact number in this letter?</Label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="share_contact" checked={form?.share_contact_number === 'no'} onChange={() => update('share_contact_number', 'no')} /> No
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="share_contact" checked={form?.share_contact_number === 'yes'} onChange={() => update('share_contact_number', 'yes')} /> Yes
              </label>
            </div>
            {form?.share_contact_number === 'yes' && (
              <input type="tel" value={form?.contact_number} onChange={(e) => update('contact_number', e.target.value)} placeholder="Contact number to display" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={inputStyle} />
            )}
          </div>

          {result && (
            <div className="rounded-lg p-3 text-sm mb-4 mt-2" style={result?.success ? { background: '#dcfce7', color: '#16a34a' } : { background: '#fee2e2', color: '#DC143C' }}>
              {result?.success ? (
                <>Sent successfully{result?.po_number ? ` — ${result.po_number}` : ''}{result?.invoice_number ? ` — ${result.invoice_number}` : ''}. <a href={result?.pdf_url} target="_blank" rel="noreferrer" className="underline font-semibold">View PDF</a></>
              ) : result?.message}
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={!isValid || sending}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold text-white transition mt-2"
            style={{ background: (!isValid || sending) ? '#9ca3af' : '#1e4a72', cursor: (!isValid || sending) ? 'not-allowed' : 'pointer' }}
          >
            {sending ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Send" size={16} />}
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FroEmailCenter;
